import { MapPin, Navigation } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "../lib/motion";
import { PinMark } from "./PinMark";

const AREAS = [
  "Western Pennsylvania",
  "Northwestern West Virginia",
  "Morgantown and surrounding communities",
  "Local and long-distance destinations",
];

// Decorative pins scattered on the stylized map
const PINS = [
  { top: "28%", left: "34%", label: "Western PA", delay: 0.1 },
  { top: "46%", left: "58%", label: "Morgantown", delay: 0.25 },
  { top: "62%", left: "40%", label: "NW West Virginia", delay: 0.4 },
];

export const ServiceArea = () => (
  <section id="area" className="relative bg-white py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <Reveal>
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-[#0891B2] mb-4">
            Service Area
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#3B3A4E] leading-tight">
            Serving Western Pennsylvania and Northern West Virginia
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-5 text-base sm:text-lg text-[#5C5975] leading-relaxed">
            Recovery Rides currently provides local and long-distance transportation throughout Western
            Pennsylvania, Northwestern West Virginia, and the Morgantown area.
          </p>
        </Reveal>
        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          {AREAS.map((a, i) => (
            <Reveal key={a} delay={0.1 + i * 0.06}>
              <div className="flex items-center gap-3 rounded-2xl bg-[#FAFAF9] border border-[#3B3A4E]/8 px-4 py-3.5">
                <MapPin size={18} className="text-[#0891B2] shrink-0" />
                <span className="text-sm font-medium text-[#3B3A4E]">{a}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-6 flex items-start gap-3 rounded-2xl brand-gradient-soft border border-[#00B4B4]/20 p-5">
            <Navigation size={20} className="text-[#0891B2] shrink-0 mt-0.5" />
            <p className="text-sm sm:text-base text-[#3B3A4E] leading-relaxed">
              Outside the immediate service area? Contact us. Long-distance transportation may be available
              depending on scheduling and driver availability.
            </p>
          </div>
        </Reveal>
      </div>

      {/* Stylized service-radius map */}
      <Reveal delay={0.1}>
        <div className="relative aspect-square rounded-[2rem] rounded-bl-[4rem] overflow-hidden bg-[#1F1D2B] shadow-2xl shadow-[#3B3A4E]/25">
          {/* soft grid */}
          <div
            className="absolute inset-0 opacity-[0.15]"
            style={{
              backgroundImage:
                "linear-gradient(#ffffff33 1px, transparent 1px), linear-gradient(90deg, #ffffff33 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />
          {/* radius rings */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            {[520, 380, 240, 120].map((s, i) => (
              <motion.div
                key={s}
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] }}
                className="absolute rounded-full border border-[#1AD1B9]/25"
                style={{
                  width: s,
                  height: s,
                  left: -s / 2,
                  top: -s / 2,
                  background: i === 3 ? "rgba(26,209,185,0.12)" : "transparent",
                }}
              />
            ))}
          </div>

          {/* location pins */}
          {PINS.map((p) => (
            <motion.div
              key={p.label}
              initial={{ opacity: 0, y: -14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: p.delay, ease: [0.22, 1, 0.36, 1] }}
              className="absolute flex flex-col items-center"
              style={{ top: p.top, left: p.left }}
            >
              <PinMark id={`areaPin-${p.label.replace(/\s/g, "")}`} className="h-9 w-9 drop-shadow-lg" />
              <span className="mt-1.5 text-[11px] font-semibold text-white/90 whitespace-nowrap bg-[#1F1D2B]/70 backdrop-blur px-2 py-0.5 rounded-full">
                {p.label}
              </span>
            </motion.div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);
